import type { UserActivity } from '../util';

import { differenceInSeconds, parseISO } from 'date-fns';
import { css, html } from 'react-strict-dom';
import { Avatar, MaybeLink, Text, TimerCompanyLogo } from 'src/components';
import { Color, Style } from 'src/constants';
import { color } from 'src/constants/color.css';
import { formatElapsed, formatTimeSpent } from 'src/util';

import { getTopSegments } from '../util';
import ActivityBar from './ActivityBar';

/** How many pieces of work are named under the bar before the rest is a count. */
const NAMED_SEGMENTS = 3;

type Props = {
  activity: UserActivity;
  colors: Record<string, string>;
  /** Ticks the running clock, held by the page so one tick covers every row. */
  now: Date;
  isMe: boolean;
  /** A past day is finished, so nothing is said about right now. */
  isToday: boolean;
};

/**
 * One person's day: what they have banked, what they are on right now, and how
 * the time splits across the work.
 */
export default function ActivityUserRow({
  activity,
  colors,
  now,
  isMe,
  isToday,
}: Props) {
  const { user, minutes, running } = activity;
  const { shown, remaining } = getTopSegments(activity, NAMED_SEGMENTS);

  const elapsedSeconds = running
    ? Math.max(differenceInSeconds(now, parseISO(running.startedAt)), 0)
    : 0;
  const hasLogged = minutes > 0 || running !== null;
  const idleNote = running ? null : getIdleNote(hasLogged, isToday);

  return (
    <html.div style={[Style.col, styles.row]}>
      <html.div style={[Style.row, Style.alignCenter, Style.spaceBetween]}>
        <html.div style={[Style.row, Style.alignCenter, styles.who]}>
          <Avatar user={user} size={28} />
          <Text typeface="body.small.medium" color="neutral100">
            {`${user.firstName} ${user.lastName}${isMe ? ' (you)' : ''}`}
          </Text>
        </html.div>

        <html.div style={[Style.row, Style.alignCenter, styles.totals]}>
          {running && (
            <html.span style={[Style.row, Style.alignCenter, styles.clock]}>
              <html.span style={styles.dot} />
              <Text typeface="caption.semiBold" color="green">
                {formatElapsed(elapsedSeconds)}
              </Text>
            </html.span>
          )}
          <Text
            typeface="body.small.medium"
            color={hasLogged ? 'neutral100' : 'neutral40'}
          >
            {formatTimeSpent(minutes) ?? '--'}
          </Text>
        </html.div>
      </html.div>

      {running && (
        <html.div style={[Style.row, Style.alignCenter, styles.now]}>
          <TimerCompanyLogo
            company={running.target.company}
            title={running.target.title}
          />
          <MaybeLink to={running.target.path}>
            <Text typeface="caption.medium" color="neutral100">
              {running.target.title}
            </Text>
          </MaybeLink>
          {running.target.reference && (
            <Text typeface="caption.medium" color="neutral60">
              {running.target.reference}
            </Text>
          )}
        </html.div>
      )}
      {idleNote && (
        <Text typeface="caption.medium" color="neutral40">
          {idleNote}
        </Text>
      )}

      {hasLogged && (
        <>
          <ActivityBar
            segments={activity.segments}
            runningMinutes={running ? Math.floor(elapsedSeconds / 60) : null}
            runningCompanyId={running?.target.company?.id ?? null}
            colors={colors}
          />

          <html.div style={[Style.row, Style.wrap, styles.breakdown]}>
            {shown.map((segment) => (
              <html.span
                key={segment.key}
                style={[Style.row, Style.alignCenter, styles.item]}
              >
                <html.span
                  style={[
                    styles.swatch,
                    styles.swatchColor(
                      (segment.companyId && colors[segment.companyId]) ||
                        Color.neutral40
                    ),
                  ]}
                />
                <Text typeface="caption.medium" color="neutral60">
                  {segment.label}
                </Text>
                <Text typeface="caption.medium" color="neutral100">
                  {formatTimeSpent(segment.minutes) ?? '--'}
                </Text>
              </html.span>
            ))}
            {remaining > 0 && (
              <Text typeface="caption.medium" color="neutral40">
                {`+${remaining} more`}
              </Text>
            )}
          </html.div>
        </>
      )}
    </html.div>
  );
}

function getIdleNote(hasLogged: boolean, isToday: boolean): string | null {
  if (!hasLogged) return isToday ? 'Nothing logged yet' : 'Nothing logged';
  return isToday ? 'No timer running' : null;
}

const styles = css.create({
  row: {
    gap: 10,
    paddingBlock: 14,
    paddingInline: 16,
  },
  who: {
    gap: 10,
    minWidth: 0,
  },
  totals: {
    gap: 8,
    flexShrink: 0,
  },
  clock: {
    gap: 6,
    paddingInline: 8,
    paddingBlock: 2,
    borderRadius: 999,
    backgroundColor: color.greenBg,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 999,
    backgroundColor: color.green,
  },
  now: {
    gap: 8,
    minWidth: 0,
  },
  breakdown: {
    gap: 14,
    rowGap: 6,
  },
  item: {
    gap: 6,
  },
  swatch: {
    width: 8,
    height: 8,
    borderRadius: 999,
    flexShrink: 0,
  },
  swatchColor: (backgroundColor: string) => ({ backgroundColor }),
});
